/**
 * DashboardCardItem — renders a single dashboard card in either
 * "detailed" (icon + titolo + sottotitolo + una riga metriche) or "compact" (barra 88px).
 */
import { Link } from "react-router-dom";
import {
  FolderOpen,
  Bed,
  Users,
  Building2,
  UserCog,
  ShoppingCart,
  ChevronRight,
  Package,
  AlertTriangle,
  ShieldCheck,
  Newspaper,
  Receipt,
  CalendarDays,
  FileBox,
  ClipboardList,
  BookOpen,
  Bell,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";

const PANORAMICA_DETAIL_MIN_H = "min-h-[172px]";

// ---------------------------------------------------------------------------
// Compact card (gradient bar, h-[88px])
// ---------------------------------------------------------------------------
function CompactCard({ to, gradient, icon: Icon, title, subtitle, badge, ringClass, onClick }) {
  const inner = (
    <Card className={`card-base ${gradient} text-white border-0 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 min-h-[88px] sm:h-[88px]`}>
      <CardContent className="p-4 h-full flex items-center">
        <div className="flex items-center justify-between w-full gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-bold flex items-center gap-2 flex-wrap leading-tight">
                {title}
                {badge}
              </h3>
              <p className="text-white/80 text-[11px] leading-snug">{subtitle}</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-white/70 flex-shrink-0" />
        </div>
      </CardContent>
    </Card>
  );

  if (onClick) {
    return (
      <button onClick={onClick} type="button" className={`block min-w-0 w-full text-left ${ringClass || ""}`}>
        {inner}
      </button>
    );
  }

  return (
    <Link to={to} className={`block min-w-0 ${ringClass || ""}`}>
      {inner}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Detailed card — solo: icona, titolo, sottotitolo, una riga metriche
// ---------------------------------------------------------------------------
function DetailedCard({
  to,
  gradient,
  border,
  iconBg,
  icon: Icon,
  title,
  subtitle,
  metrics,
  minHeight = PANORAMICA_DETAIL_MIN_H,
  onClick,
  dataTestId,
  cardDataTestId,
}) {
  const inner = (
    <Card
      data-testid={cardDataTestId}
      className={`card-base h-full flex flex-col ${minHeight} ${gradient} ${border} shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300`}
    >
      <CardContent className="p-4 flex flex-col gap-1.5 min-h-0">
        <div className={`w-14 h-14 rounded-full ${iconBg} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-7 h-7 text-white" />
        </div>
        <div className="min-w-0 space-y-0.5 pt-0.5">
          <h3 className="text-base font-bold leading-tight">{title}</h3>
          <p className="text-[11px] opacity-70 line-clamp-2 leading-snug">{subtitle}</p>
        </div>
        <div className="min-w-0 mt-1">{metrics}</div>
      </CardContent>
    </Card>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        data-testid={dataTestId}
        className="block h-full min-h-0 w-full cursor-pointer text-left"
      >
        {inner}
      </button>
    );
  }
  if (to) {
    return (
      <Link to={to} className="block h-full min-h-0" data-testid={dataTestId}>
        {inner}
      </Link>
    );
  }
  return inner;
}

/** Una sola riga: badge/contatori/contenuti chiave del blocco */
function MetricsRow({ children, colorClass = "ring-slate-100" }) {
  return (
    <div
      className={`flex w-full min-w-0 flex-nowrap items-center gap-2 overflow-hidden rounded-lg bg-white/80 px-2 py-1 shadow-sm ring-1 ${colorClass}`}
    >
      <div className="flex min-w-0 flex-1 flex-nowrap items-center gap-2 overflow-hidden text-[10px] leading-tight text-slate-800 [&_strong]:font-bold">
        {children}
      </div>
    </div>
  );
}

function truncateSpan(text, key) {
  return (
    <span key={key} className="min-w-0 truncate">
      {text}
    </span>
  );
}

// ---------------------------------------------------------------------------

function renderCalendario({ size, cardData }) {
  const { attivitaOggi, onOpenFullCalendar } = cardData;
  const today = new Date();
  const giorni = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];
  const mesi = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
  const att = attivitaOggi || [];

  if (size === "compact") {
    return (
      <CompactCard
        onClick={onOpenFullCalendar}
        gradient="bg-gradient-to-r from-emerald-600 via-emerald-600 to-teal-600"
        icon={CalendarDays}
        title="Calendario & Attività"
        subtitle={att.length === 0 ? "Nessun evento oggi" : `${att.length} event${att.length === 1 ? "o" : "i"} oggi`}
      />
    );
  }

  const sub =
    `${giorni[today.getDay()]} ${today.getDate()} ${mesi[today.getMonth()].toLowerCase()}` +
    ` ${today.getFullYear()}`;

  let metricTxt;
  if (att.length === 0) metricTxt = "Oggi: nessun evento";
  else if (att.length === 1) metricTxt = att[0].ora ? `${att[0].titolo} (${att[0].ora})` : att[0].titolo;
  else metricTxt = `Oggi: ${att.length} eventi`;

  return (
    <div className="h-full min-h-0">
      <DetailedCard
        onClick={onOpenFullCalendar}
        dataTestId="calendario-widget-btn"
        cardDataTestId="calendario-attivita-card"
        gradient="bg-gradient-to-br from-amber-50 via-yellow-50 to-emerald-50"
        border="border border-amber-300"
        iconBg="bg-emerald-500"
        icon={CalendarDays}
        title={<span className="text-slate-900">Calendario</span>}
        subtitle={<span className="text-emerald-800/80">{sub}</span>}
        metrics={
          <MetricsRow colorClass="ring-emerald-100">
            {truncateSpan(metricTxt)}
          </MetricsRow>
        }
      />
    </div>
  );
}

function renderFascicoli({ size, cardData }) {
  const { fascicoliStat, alertPianiTerapeuticiCount = 0 } = cardData;

  if (size === "compact") {
    return (
      <CompactCard
        to="/fascicoli-assistenziali"
        gradient="bg-gradient-to-r from-violet-600 via-purple-600 to-purple-700"
        icon={FolderOpen}
        title="Fascicoli Assistenziali"
        subtitle={`${fascicoliStat?.ospiti ?? 0} ospiti · ${fascicoliStat?.residenti ?? 0} residenti`}
        badge={
          fascicoliStat?.alerts > 0 ? (
            <Badge className="bg-amber-300 text-amber-950 text-[10px] font-bold animate-pulse px-1.5 py-0.5">{fascicoliStat.alerts}</Badge>
          ) : undefined
        }
      />
    );
  }

  const parts = [
    `${fascicoliStat?.ospiti ?? 0} ospiti · ${fascicoliStat?.pai ?? 0} PAI · ${fascicoliStat?.residenti ?? 0} residenti`,
  ];
  if (alertPianiTerapeuticiCount > 0) {
    parts.push(
      `${alertPianiTerapeuticiCount} ${alertPianiTerapeuticiCount === 1 ? "scadenza" : "scadenze"}`,
    );
  }

  return (
    <DetailedCard
      to="/fascicoli-assistenziali"
      gradient="bg-gradient-to-br from-violet-50 to-purple-100"
      border="border border-violet-300"
      iconBg="bg-purple-500"
      icon={FolderOpen}
      title={<span className="text-purple-900">Fascicoli Assistenziali</span>}
      subtitle={<span className="text-purple-700/70">Documentazione assistenziale</span>}
      metrics={
        <MetricsRow colorClass="ring-purple-100">
          {(fascicoliStat?.alerts ?? 0) > 0 && (
            <Badge className="shrink-0 bg-amber-500 px-1.5 py-0 text-[9px] font-bold text-white">
              <AlertTriangle className="mr-0.5 inline h-2.5 w-2.5" />
              {fascicoliStat.alerts}
            </Badge>
          )}
          {truncateSpan(parts.join(" · "), "fp")}
        </MetricsRow>
      }
    />
  );
}

function renderAlloggiati({ size, cardData }) {
  const { alertReinvio, alloggiatiWebStat } = cardData;
  const hasAlert = alertReinvio?.has_alert;

  if (size === "compact") {
    return (
      <CompactCard
        to="/alloggiati-web"
        gradient={
          hasAlert ? "bg-gradient-to-r from-amber-500 via-orange-500 to-orange-600" : "bg-gradient-to-r from-cyan-600 via-cyan-600 to-sky-600"
        }
        icon={ShieldCheck}
        title="Alloggiati Web"
        subtitle={hasAlert ? `${alertReinvio.count_scaduti ?? 0} da reinviare` : `${alloggiatiWebStat?.ospiti ?? 0} ospiti registrati`}
        badge={
          hasAlert ? <Badge className="bg-white/30 px-1.5 py-0 text-[10px] font-bold text-white">ALERT</Badge> : undefined
        }
      />
    );
  }

  let line = "";
  if (hasAlert) {
    const p = [];
    if (alertReinvio.count_scaduti > 0) p.push(`${alertReinvio.count_scaduti} reinviare`);
    if (alertReinvio.count_in_scadenza > 0) p.push(`${alertReinvio.count_in_scadenza} in scadenza 48h`);
    if (typeof alloggiatiWebStat?.ospiti === "number") p.push(`${alloggiatiWebStat.ospiti} schede`);
    line = p.join(" · ") || "Controlla invii";
  } else if (alloggiatiWebStat?.prossimiInvii?.length > 0) {
    line = `Prossimi: ${alloggiatiWebStat.prossimiInvii.join(" · ")}`;
    if ((alertReinvio?.count_in_scadenza ?? 0) > 0)
      line += ` · ${alertReinvio.count_in_scadenza} in scadenza 48h`;
  } else {
    line =
      ((alertReinvio?.count_in_scadenza ?? 0) > 0 ? `${alertReinvio.count_in_scadenza} in scadenza 48h · ` : "") +
      "Nessun invio programmato";
  }

  const cardGradient = hasAlert ? "bg-gradient-to-br from-amber-50 to-orange-100" : "bg-gradient-to-br from-cyan-50 to-sky-100";
  const cardBorder = hasAlert ? "border border-amber-400" : "border border-cyan-300";
  const iconBg = hasAlert ? "bg-amber-500" : "bg-cyan-600";

  return (
    <DetailedCard
      to="/alloggiati-web"
      gradient={cardGradient}
      border={cardBorder}
      iconBg={iconBg}
      icon={ShieldCheck}
      title={<span className={hasAlert ? "text-amber-900" : "text-cyan-900"}>Alloggiati Web</span>}
      subtitle={<span className={hasAlert ? "text-amber-700/70" : "text-cyan-700/70"}>Invio schede Questura</span>}
      metrics={
        <MetricsRow colorClass={hasAlert ? "ring-amber-200" : "ring-cyan-100"}>
          {hasAlert && (
            <Badge className="shrink-0 bg-amber-500 px-1.5 py-0 text-[9px] font-bold text-white">
              <AlertTriangle className="mr-0.5 inline h-2.5 w-2.5" />
              !
            </Badge>
          )}
          {truncateSpan(line, "alo")}
        </MetricsRow>
      }
    />
  );
}

function renderPostiLiberi({ size, cardData }) {
  const { postiLiberi, leadCompatibiliCount = 0 } = cardData;

  if (size === "compact") {
    return (
      <CompactCard
        to="/posti-liberi"
        gradient="bg-gradient-to-r from-sky-600 via-sky-600 to-blue-600"
        icon={Bed}
        title="Camere e posti letto"
        subtitle={`${postiLiberi ?? 0} posti liberi`}
      />
    );
  }

  return (
    <DetailedCard
      to="/posti-liberi"
      gradient="bg-gradient-to-br from-sky-50 to-blue-100"
      border="border border-sky-300"
      iconBg="bg-sky-500"
      icon={Bed}
      title={<span className="text-sky-900">Camere e posti letto</span>}
      subtitle={<span className="text-sky-700/70">Segnala disponibilità</span>}
      metrics={
        <MetricsRow colorClass="ring-sky-100">
          {postiMetric(postiLiberi, leadCompatibiliCount)}
        </MetricsRow>
      }
    />
  );
}

function postiMetric(postiLiberi, leadCompatibiliCount) {
  const nodes = [<strong key="p">{postiLiberi ?? 0}</strong>, " liberi"];
  if (leadCompatibiliCount > 0) {
    nodes.push(" · ", <strong key="l">{leadCompatibiliCount}</strong>, " lead compatibili");
  }
  return <span className="min-w-0 truncate">{nodes}</span>;
}

function renderOspitiRete({ size, cardData }) {
  const { ospitiInRete } = cardData;
  const tg = ospitiInRete?.totale_gettone;
  const gm = ospitiInRete?.gettone_maturato;
  const totaleNumerico = Number(tg);
  const showTotGettone =
    tg != null && !Number.isNaN(totaleNumerico) && totaleNumerico > 0 && totaleNumerico !== Number(gm ?? 0);

  if (size === "compact") {
    return (
      <CompactCard
        to="/ospiti-in-rete"
        gradient="bg-gradient-to-r from-indigo-600 via-indigo-600 to-blue-600"
        icon={Users}
        title="Ospiti in Rete"
        subtitle={`${ospitiInRete?.count ?? 0} ospiti · Gettone €${ospitiInRete?.gettone_maturato?.toLocaleString("it-IT") ?? "0"}`}
      />
    );
  }

  const parts = [
    `${ospitiInRete?.count ?? 0} ospiti`,
    `gettone € ${ospitiInRete?.gettone_maturato?.toLocaleString("it-IT") || "0"}`,
  ];
  if (showTotGettone) parts.push(`tot. ciclo € ${totaleNumerico.toLocaleString("it-IT")}`);

  return (
    <DetailedCard
      to="/ospiti-in-rete"
      dataTestId="card-ospiti-rete"
      gradient="bg-gradient-to-br from-indigo-50 to-blue-100"
      border="border border-indigo-300"
      iconBg="bg-indigo-500"
      icon={Users}
      title={<span className="text-indigo-900">Ospiti in Rete</span>}
      subtitle={<span className="text-indigo-700/70">Invia l&apos;ospite</span>}
      metrics={
        <MetricsRow colorClass="ring-indigo-100">{truncateSpan(parts.join(" · "), "or")}</MetricsRow>
      }
    />
  );
}

function renderPersonale({ size, cardData }) {
  const { personaleRichieste, personaleRichiesteTotale = 0 } = cardData;
  const chiuse = Math.max(0, personaleRichiesteTotale - personaleRichieste);

  if (size === "compact") {
    return (
      <CompactCard
        to="/personale"
        gradient="bg-gradient-to-r from-amber-500 via-amber-500 to-yellow-500"
        icon={UserCog}
        title="Personale"
        subtitle={personaleRichieste > 0 ? `${personaleRichieste} richieste attive` : "Nessuna richiesta"}
      />
    );
  }

  const parts = [`${personaleRichieste} da gestire`];
  if (personaleRichiesteTotale > 0) parts.push(`${personaleRichiesteTotale} in elenco`);
  if (chiuse > 0) parts.push(`${chiuse} concluse`);

  return (
    <DetailedCard
      to="/personale"
      dataTestId="card-personale"
      gradient="bg-gradient-to-br from-yellow-50 to-amber-100"
      border="border border-yellow-300"
      iconBg="bg-amber-500"
      icon={UserCog}
      title={<span className="text-amber-900">Personale</span>}
      subtitle={<span className="text-amber-700/70">Gestione richieste</span>}
      metrics={
        <MetricsRow colorClass="ring-amber-100">{truncateSpan(parts.join(" · "), "pe")}</MetricsRow>
      }
    />
  );
}

function renderDiarioConsegne({ size, cardData }) {
  const { diarioOggiStat } = cardData;

  if (size === "compact") {
    return (
      <CompactCard
        to="/diario-consegne"
        gradient="bg-gradient-to-r from-emerald-600 via-emerald-600 to-teal-600"
        icon={BookOpen}
        title="Diario consegne"
        subtitle={`${diarioOggiStat?.consegne ?? 0} consegne · ${diarioOggiStat?.segnalazioni ?? 0} segnalazioni`}
      />
    );
  }

  const c = diarioOggiStat?.consegne ?? 0;
  const s = diarioOggiStat?.segnalazioni ?? 0;

  return (
    <DetailedCard
      to="/diario-consegne"
      dataTestId="card-diario-consegne"
      gradient="bg-gradient-to-br from-emerald-50 to-green-100"
      border="border border-emerald-300"
      iconBg="bg-emerald-500"
      icon={BookOpen}
      title={<span className="text-emerald-900">Diario consegne</span>}
      subtitle={<span className="text-emerald-700/70">Registro turni di oggi</span>}
      metrics={
        <MetricsRow colorClass="ring-emerald-100">
          {truncateSpan(`${c} ${c === 1 ? "consegna" : "consegne"} · ${s} ${s === 1 ? "segnalazione" : "segnalazioni"}`, "dc")}
        </MetricsRow>
      }
    />
  );
}

function renderImmobiliare({ size, cardData }) {
  const { hasNewImmobili, immobiliCount, kpi } = cardData;

  if (size === "compact") {
    return (
      <CompactCard
        to="/immobiliare"
        gradient={
          hasNewImmobili ? "bg-gradient-to-r from-red-500 via-red-500 to-orange-600" : "bg-gradient-to-r from-[#4068E8] via-[#4068E8] to-[#3557D4]"
        }
        icon={Building2}
        title="Immobiliare"
        subtitle={`${immobiliCount || kpi?.opportunita_immobiliari || 0} opportunità attive`}
        badge={hasNewImmobili ? <Badge className="bg-white/30 px-1.5 py-0 text-[10px] font-bold text-white">NUOVO</Badge> : undefined}
      />
    );
  }

  const opp = immobiliCount || kpi?.opportunita_immobiliari || 0;
  const gradient = hasNewImmobili ? "bg-gradient-to-br from-red-50 to-orange-100" : "bg-gradient-to-br from-blue-50 to-sky-100";
  const border = hasNewImmobili ? "border border-red-300" : "border border-blue-300";
  const iconBg = hasNewImmobili ? "bg-red-500" : "bg-[#4068E8]";

  return (
    <DetailedCard
      to="/immobiliare"
      dataTestId="card-immobiliare"
      gradient={gradient}
      border={border}
      iconBg={iconBg}
      icon={Building2}
      title={<span className={hasNewImmobili ? "text-red-900" : "text-blue-900"}>Immobiliare</span>}
      subtitle={<span className={hasNewImmobili ? "text-red-700/70" : "text-blue-700/70"}>Opportunità</span>}
      metrics={
        <MetricsRow colorClass={hasNewImmobili ? "ring-red-100" : "ring-blue-100"}>
          {hasNewImmobili && (
            <Badge className="shrink-0 bg-red-500 px-1.5 py-0 text-[9px] font-bold text-white">
              <Bell className="mr-0.5 inline h-2.5 w-2.5" />
              NUOVO
            </Badge>
          )}
          {truncateSpan(`${opp} attive`, "im")}
        </MetricsRow>
      }
    />
  );
}

function renderFatturazione({ size, cardData }) {
  const { strutturaDisplayName } = cardData;

  if (size === "detailed") {
    return (
      <DetailedCard
        to="/fatturazione"
        dataTestId="card-fatturazione"
        gradient="bg-gradient-to-br from-teal-50 via-emerald-50 to-teal-100"
        border="border border-teal-300"
        iconBg="bg-teal-600"
        icon={Receipt}
        title={<span className="text-teal-900">Fatturazione</span>}
        subtitle={
          <span className="text-teal-700/70">
            Fatturazione elettronica · <span className="font-semibold text-teal-800">{strutturaDisplayName || "—"}</span>
          </span>
        }
        metrics={
          <MetricsRow colorClass="ring-teal-100">
            {truncateSpan("Elenco · XML · PDF · invio SDI", "fa")}
          </MetricsRow>
        }
      />
    );
  }

  return (
    <CompactCard
      to="/fatturazione"
      gradient="bg-gradient-to-r from-teal-600 via-teal-600 to-emerald-600"
      icon={Receipt}
      title="Fatturazione"
      subtitle={`Elenco fatture · ${strutturaDisplayName || ""}`}
    />
  );
}

function renderNewsletter({ size, cardData }) {
  const { unreadCount, newslettersTotal = 0 } = cardData;
  const nuoveBadge =
    unreadCount > 0 ? (
      <Badge className="bg-amber-300 text-amber-950 text-[10px] font-bold animate-pulse px-1.5 py-0.5">
        {unreadCount} NUOV{unreadCount === 1 ? "A" : "E"}
      </Badge>
    ) : null;

  if (size === "detailed") {
    const line =
      unreadCount > 0
        ? `${unreadCount} da leggere · ${newslettersTotal} in elenco`
        : `${newslettersTotal} in elenco`;

    return (
      <DetailedCard
        to="/newsletter"
        dataTestId="card-newsletter"
        gradient="bg-gradient-to-br from-teal-50 via-emerald-50 to-teal-100"
        border="border border-teal-300"
        iconBg="bg-teal-600"
        icon={Newspaper}
        title={<span className="text-teal-900">Newsletter</span>}
        subtitle={<span className="text-teal-700/70">Comunicazioni e circolari dalle strutture</span>}
        metrics={
          <MetricsRow colorClass="ring-teal-100">
            {truncateSpan(line, "nw")}
          </MetricsRow>
        }
      />
    );
  }

  return (
    <CompactCard
      to="/newsletter"
      gradient="bg-gradient-to-r from-teal-600 via-teal-600 to-emerald-600"
      icon={Newspaper}
      title="Newsletter"
      subtitle="Comunicazioni strutture"
      badge={nuoveBadge}
    />
  );
}

function renderDeblistering({ size, cardData }) {
  const rip = cardData?.deblisteringRiepilogo;
  if (size === "detailed") {
    const line = rip
      ? [
          `${rip.ospiti_attivi ?? 0} aderenti`,
          `${rip.fatture_da_assegnare ?? 0} fatt. da associare`,
          `${rip.fatture_da_inserire ?? 0} righe senza importo`,
          typeof rip.ricavo_mensile_stimato === "number" && rip.ricavo_mensile_stimato > 0
            ? `stim. € ${rip.ricavo_mensile_stimato.toLocaleString("it-IT", { minimumFractionDigits: 2 })}`
            : null,
        ]
          .filter(Boolean)
          .join(" · ")
      : "Nessun dato — apri la sezione";

    return (
      <DetailedCard
        to="/deblistering"
        gradient="bg-gradient-to-br from-cyan-50 to-sky-100"
        border="border border-cyan-300"
        iconBg="bg-cyan-600"
        icon={Package}
        title={<span className="text-cyan-900">Deblistering</span>}
        subtitle={<span className="text-cyan-700/70">Gestione blister farmaci</span>}
        metrics={
          <MetricsRow colorClass="ring-cyan-100">{truncateSpan(line, "db")}</MetricsRow>
        }
      />
    );
  }
  return (
    <CompactCard
      to="/deblistering"
      gradient="bg-gradient-to-r from-cyan-600 via-cyan-600 to-sky-600"
      icon={Package}
      title="Deblistering"
      subtitle="Gestione blister farmaci"
    />
  );
}

function renderRisparmio({ size, cardData }) {
  const { risparmioData, promoAttive } = cardData;
  const promoRingClass = promoAttive?.length > 0 ? "ring-2 ring-amber-300 ring-offset-2 rounded-xl" : "";

  if (size === "detailed") {
    const parts = [`€ ${risparmioData?.risparmio_totale?.toFixed(2) || "0.00"} risparmiati`];
    if (typeof risparmioData?.num_ordini === "number") parts.push(`${risparmioData.num_ordini} ordini`);
    if ((promoAttive?.length ?? 0) > 0) parts.push(`${promoAttive.length} promo attive`);

    return (
      <DetailedCard
        to="/risparmio"
        gradient="bg-gradient-to-br from-blue-50 to-indigo-100"
        border="border border-blue-300"
        iconBg="bg-blue-600"
        icon={ShoppingCart}
        title={<span className="text-blue-900">Risparmio insieme</span>}
        subtitle={<span className="text-blue-700/70">Acquisti condivisi</span>}
        metrics={
          <MetricsRow colorClass="ring-blue-100">
            {(promoAttive?.length ?? 0) > 0 && (
              <Badge className="shrink-0 bg-amber-300 px-1.5 py-0 text-[9px] font-bold text-amber-950">PROMO</Badge>
            )}
            {truncateSpan(parts.join(" · "), "ri")}
          </MetricsRow>
        }
      />
    );
  }

  return (
    <Link to="/risparmio" className={`block min-w-0 ${promoRingClass}`} data-testid="card-risparmio">
      <Card className="card-base bg-gradient-to-r from-blue-600 via-blue-600 to-indigo-600 text-white border-0 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 min-h-[88px] sm:h-[88px]">
        <CardContent className="p-4 h-full flex items-center">
          <div className="flex items-center justify-between w-full gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <ShoppingCart className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-bold flex items-center gap-2 flex-wrap leading-tight">
                  Risparmio insieme
                  {promoAttive?.length > 0 && (
                    <Badge className="bg-amber-300 text-amber-950 text-[10px] font-bold animate-pulse shadow-sm shrink-0">PROMO</Badge>
                  )}
                </h3>
                <p className="text-white/80 text-[11px] leading-snug">
                  <strong className="text-white font-semibold">€ {risparmioData?.risparmio_totale?.toFixed(2) || "0.00"}</strong> risparmiati
                  {typeof risparmioData?.num_ordini === "number" && (
                    <>
                      {" "}
                      · <strong className="text-white font-semibold">{risparmioData.num_ordini}</strong>{" "}
                      {risparmioData.num_ordini === 1 ? "ordine" : "ordini"}
                    </>
                  )}
                </p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-white/70 flex-shrink-0" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function renderDocumenti({ size, cardData }) {
  if (size === "detailed") {
    return (
      <DetailedCard
        to="/documenti"
        gradient="bg-gradient-to-br from-emerald-50 to-teal-100"
        border="border border-emerald-300"
        iconBg="bg-emerald-600"
        icon={FileBox}
        title={<span className="text-emerald-900">Documenti</span>}
        subtitle={<span className="text-emerald-700/70">Modelli e normative</span>}
        metrics={
          <MetricsRow colorClass="ring-emerald-100">{truncateSpan("CSA · RSA · circolari · scarica", "do")}</MetricsRow>
        }
      />
    );
  }
  return (
    <CompactCard
      to="/documenti"
      gradient="bg-gradient-to-r from-emerald-600 via-emerald-600 to-teal-600"
      icon={FileBox}
      title="Documenti"
      subtitle="Modelli, normative e documenti di struttura"
    />
  );
}

function renderPrimaNota({ size, cardData }) {
  if (size === "detailed") {
    return (
      <DetailedCard
        to="/prima-nota-ospiti"
        gradient="bg-gradient-to-br from-rose-50 to-red-100"
        border="border border-rose-300"
        iconBg="bg-rose-500"
        icon={ClipboardList}
        title={<span className="text-rose-900">Prima Nota Ospiti</span>}
        subtitle={<span className="text-rose-700/70">Consumi e spese extra</span>}
        metrics={
          <MetricsRow colorClass="ring-rose-100">{truncateSpan("Extra · consumi · magazzinetto · report", "pn")}</MetricsRow>
        }
      />
    );
  }
  return (
    <CompactCard
      to="/prima-nota-ospiti"
      gradient="bg-gradient-to-r from-rose-500 via-rose-500 to-red-500"
      icon={ClipboardList}
      title="Prima Nota Ospiti"
      subtitle="Consumi e spese extra"
    />
  );
}

function renderArredo({ size, cardData }) {
  const comingSoonBadge = (
    <Badge className="bg-white/25 text-white text-[10px] font-semibold px-1.5 py-0.5 ring-1 ring-white/30">PRESTO</Badge>
  );

  if (size === "detailed") {
    return (
      <DetailedCard
        to="/arredo"
        gradient="bg-gradient-to-br from-amber-50 to-orange-100"
        border="border border-amber-300"
        iconBg="bg-orange-500"
        icon={Bed}
        title={<span className="text-amber-900">Arredo</span>}
        subtitle={<span className="text-amber-700/70">Gestione arredo struttura</span>}
        metrics={
          <MetricsRow colorClass="ring-amber-100">
            <Badge className="shrink-0 bg-amber-200 px-1.5 py-0 text-[9px] font-semibold text-amber-900">PRESTO</Badge>
            {truncateSpan("Funzionalità in arrivo", "ar")}
          </MetricsRow>
        }
      />
    );
  }
  return (
    <Link to="/arredo" className="block min-w-0" data-testid="card-arredo">
      <Card className="card-base bg-gradient-to-r from-amber-500 via-orange-500 to-orange-600 text-white border-0 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 h-[88px]">
        <CardContent className="p-4 h-full flex items-center">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <Bed className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold">Arredo</h3>
                  {comingSoonBadge}
                </div>
                <p className="text-white/80 text-xs">Gestione arredo struttura</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-white/70 flex-shrink-0" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

const RENDERERS = {
  calendario: renderCalendario,
  fascicoli: renderFascicoli,
  alloggiati: renderAlloggiati,
  "posti-liberi": renderPostiLiberi,
  "ospiti-rete": renderOspitiRete,
  personale: renderPersonale,
  "diario-consegne": renderDiarioConsegne,
  immobiliare: renderImmobiliare,
  fatturazione: renderFatturazione,
  newsletter: renderNewsletter,
  deblistering: renderDeblistering,
  risparmio: renderRisparmio,
  documenti: renderDocumenti,
  "prima-nota": renderPrimaNota,
  arredo: renderArredo,
};

export default function DashboardCardItem({ cardId, size, cardData }) {
  const renderer = RENDERERS[cardId];
  if (!renderer) return null;
  return renderer({ size, cardData });
}
